import React, { useCallback, useMemo } from 'react'
import { SizableText, Stack, YStack } from '@mythical/ui'
import {
  ScrollingCarousel as ScrollingCarouselType,
  ScrollingImage,
  ScrollingStatCard,
} from 'app/blocks/landing/scrolling-carousel'
import { Container } from 'app/components/structure/Container'
import { Marquee } from 'app/components/primitive/Marquee'
import { SolitoImage } from 'solito/image'
import { mapColor } from 'app/utils/mapColors'

interface ScrollingCarouselBlockProps {
  block: ScrollingCarouselType
}

export const ScrollingCarouselBlock: React.FC<ScrollingCarouselBlockProps> = ({
  block,
}) => {
  const { items = [] } = block
  if (items.length === 0) return null

  const row1 = useMemo(
    () => items.slice(0, Math.ceil(items.length / 2)),
    [items],
  )
  const row2 = useMemo(() => items.slice(Math.ceil(items.length / 2)), [items])

  const renderItem = useCallback(
    (item: ScrollingCarouselType['items'][number]) => {
      if (item.__typename === 'ScrollingImage') {
        return <ImageItem item={item} />
      }
      return <CardItem item={item} />
    },
    [],
  )

  return (
    <Container variant="ultraWide" gap="$2" overflow="hidden">
      <Marquee
        items={row1}
        spacing={8}
        keyExtractor={(item) => item.__typename + item.id}
        renderItem={renderItem}
      />
      <Marquee
        items={row2}
        spacing={8}
        keyExtractor={(item) => item.__typename + item.id}
        renderItem={renderItem}
        scrollDirection="backward"
        emblaOptions={{
          align: 'center',
        }}
      />
    </Container>
  )
}

type CardItemProps = {
  item: ScrollingStatCard
}

const CardItem = ({ item }: CardItemProps) => {
  const bgColor = mapColor(item.backgroundColour)
  const fgColor = mapColor(item.foregroundColour)

  return (
    <YStack
      aspectRatio={16 / 9}
      h={144}
      $gtMd={{ h: 280 }}
      overflow="hidden"
      br="$5"
    >
      <YStack
        fs={1}
        fg={1}
        pos="relative"
        bg={bgColor}
        p="$3"
        jc="space-between"
        ai="flex-start"
        gap="$3"
        userSelect="none"
        $gtMd={{
          p: '$5',
        }}
      >
        <SizableText
          size="$9"
          fow="800"
          color={fgColor}
          $gtMd={{ size: '$12', fow: '800' }}
        >
          {item.statValue}
        </SizableText>
        <Stack
          px="$3"
          py="$2"
          bw={1}
          br="$pill"
          boc={fgColor}
          $gtMd={{ px: '$5', py: '$3.5', bw: 2 }}
        >
          <SizableText
            size="$2"
            fow="600"
            color={fgColor}
            $gtMd={{ size: '$4', fow: '600' }}
          >
            {item.label}
          </SizableText>
        </Stack>
      </YStack>
    </YStack>
  )
}

type ImageItemProps = {
  item: ScrollingImage
}

const ImageItem = ({ item }: ImageItemProps) => {
  return (
    <YStack
      pos="relative"
      h={144}
      aspectRatio={16 / 9}
      overflow="hidden"
      br="$5"
      userSelect="none"
      $gtMd={{ h: 280 }}
    >
      <SolitoImage
        src={item.image?.url}
        alt={item.altText || ''}
        sizes="(min-width: 1020px) 500px, 260px"
        fill
        contentFit="cover"
      />
    </YStack>
  )
}
